// Plain-text fields (names, titles, summaries, headings…) are meant to hold
// plain text, but values that come from rich-text editors, CV/AI imports or
// older records can carry HTML (e.g. "<p>Great communicator</p>"). Rendered as
// text, React shows those tags literally. This turns such a value into the
// text a reader should see.
//
// String-based (no DOM) so it behaves the same on the server and in the
// browser. Strings with no tag in them are returned untouched, so ordinary
// text like "R&D" or "a < b" is never altered.

const TAG = /<\/?[a-z][^>]*>|<!--/i;
const COMMENT = /<!--[\s\S]*?-->/g;
// Tags whose content is code, not text — dropped along with the content.
const DROP_WITH_CONTENT =
  /<(script|style|iframe|noscript|template|svg|math|object)\b[\s\S]*?<\/\1\s*>/gi;
const LINE_BREAK = /<br\s*\/?>/gi;
const BLOCK_CLOSE = /<\/(p|div|li|h[1-6]|blockquote|pre|tr)\s*>/gi;
const ANY_TAG = /<\/?[a-z][^>]*>/gi;

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

function decodeEntities(text: string): string {
  return text.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, entity: string) => {
    if (entity[0] === "#") {
      const isHex = entity[1]?.toLowerCase() === "x";
      const code = parseInt(entity.slice(isHex ? 2 : 1), isHex ? 16 : 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff
        ? String.fromCodePoint(code)
        : match;
    }
    return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
  });
}

export default function stripHtml(value?: string | null): string {
  if (typeof value !== "string" || !TAG.test(value)) {
    return value ?? "";
  }

  const text = value
    .replace(COMMENT, "")
    .replace(DROP_WITH_CONTENT, "")
    .replace(LINE_BREAK, "\n")
    .replace(BLOCK_CLOSE, "\n")
    .replace(ANY_TAG, "");

  return decodeEntities(text)
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
